import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ArrowLeft, Loader2 } from "lucide-react";

const LOGO_URL = "/logo.png";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await login(email, password);
    setSubmitting(false);
    if (result.success) {
      navigate("/dashboard");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link
          to="/"
          data-testid="back-to-home-link"
          className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-900 transition-colors duration-200 mb-8 text-sm font-manrope"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="text-center pb-2 pt-8">
            <img
              src={LOGO_URL}
              alt="Berkshire Africa"
              className="w-16 h-16 mx-auto mb-4 object-contain"
              data-testid="login-logo"
            />
            <h1 className="font-playfair text-2xl font-bold text-slate-900 tracking-tight">
              Sign In
            </h1>
            <p className="text-slate-500 text-sm font-manrope mt-1">
              Access your portfolio dashboard
            </p>
          </CardHeader>
          <CardContent className="px-8 pb-8 pt-4">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div
                  data-testid="login-error-message"
                  className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-sm font-manrope"
                >
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 text-sm font-manrope font-medium">
                  Email Address
                </Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="h-11 border-slate-200 focus:ring-2 focus:ring-blue-800 font-manrope rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 text-sm font-manrope font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="h-11 border-slate-200 focus:ring-2 focus:ring-blue-800 font-manrope rounded-sm"
                />
              </div>

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={submitting}
                className="w-full h-11 bg-[#1e3a8a] hover:bg-[#1e40af] text-white font-manrope font-semibold rounded-sm transition-colors duration-200"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-slate-400 text-xs font-manrope mt-6">
          Berkshire Africa Inc. All rights reserved.
        </p>
      </div>
    </div>
  );
}
